import {
  Draft,
  EntityState,
  PayloadAction,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { RootState } from '../store';
import { useAppSelector } from '../hooks';
import { useMemo } from 'react';
import {
  InvestigationAssetTrackBasicModel,
  InvestigationAssetType,
  InvestigationBasicModel,
  RawTrackRecord,
} from '../apis/investigationsApi';
import { DateTime } from 'luxon';

export interface AssetControl {
  visible: boolean;
}

export interface InvestigationPlaybackState {
  currentTime: number;
  isPlaying: boolean;
}

export interface InvestigationState {
  interactionStartTime: number;
  interactionAssetId?: number;
  interactionStartRawTrack: RawTrackRecord | undefined;
  currentRawTrack: RawTrackRecord | undefined;
  firstRawTrack: RawTrackRecord | undefined;
  lastRawTrack: RawTrackRecord | undefined;
  rawTrackRecords: RawTrackRecord[];
  assetControls: Record<number, AssetControl>;
  hiddenAssetIds: number[];
  visibleRawTrackIds: number[];
  firstVisibleRawTrack: RawTrackRecord | undefined;
  lastVisibleRawTrack: RawTrackRecord | undefined;
  playback: InvestigationPlaybackState;
}

export interface InvestigationEntity extends InvestigationState {
  id: string;
}

export interface InvestigationEnitityIdProps {
  siteId: string;
  investigationId: string;
}

interface RegisterInvestigationPayload extends InvestigationEnitityIdProps {
  data: Pick<InvestigationBasicModel, 'ts' | 'assets'>;
}

interface UpdateInvestigationWithRawTracksPayload extends InvestigationEnitityIdProps {
  rawTrackRecords: RawTrackRecord[];
}

interface SetAssetVisibilityAction extends InvestigationEnitityIdProps {
  assetId: number;
  visible: boolean;
}

export type StepDirection = 1 | -1; // 1 for next, -1 for previous.

interface StepRawTrackAction extends InvestigationEnitityIdProps {
  direction: StepDirection;
}

interface SetCurrentRawTrackAction extends InvestigationEnitityIdProps {
  id: number;
}

interface SetIsPlayingAction extends InvestigationEnitityIdProps {
  isPlaying: boolean;
}

interface SetCurrentTimeAction extends InvestigationEnitityIdProps {
  currentTime: number;
}

const investigationsAdapter = createEntityAdapter({
  selectId: (entity: InvestigationEntity) => entity.id,
  sortComparer: (a, b) => a.id.localeCompare(b.id),
});

const createAssetControls = (
  assets: InvestigationAssetTrackBasicModel[],
  interactionAssetId?: number, // Custom investigations will not have an interactionAssetId (all assets are nearby).
): Record<number, AssetControl> => {
  const assetControls: Record<number, AssetControl> = {};

  for (const asset of assets) {
    const { asset: assetId, typ: type } = asset;
    let visible = true;
    // All assets should be visible by default unless they are part of a logged investigation.
    if (typeof interactionAssetId !== 'undefined' && type === InvestigationAssetType.Nearby) {
      visible = false;
    }
    assetControls[assetId] = { visible };
  }

  return assetControls;
};

const getHiddenAssetIds = (assetControls: Record<number, AssetControl>): number[] => {
  const hiddenAssetControls = Object.entries(assetControls).filter(
    ([, control]: [string, AssetControl]) => !control.visible,
  );
  return hiddenAssetControls.map(([key]: [string, AssetControl]) => Number(key));
};

const getVisibleRawTrackIds = (
  rawTrackRecords: RawTrackRecord[],
  hiddenAssetIds: number[],
): number[] => {
  const visibleRawTracks = rawTrackRecords.filter(
    ({ assetId }) => !hiddenAssetIds.includes(assetId),
  );
  const visibleRawTrackIds = visibleRawTracks.map(({ id }) => id);
  return visibleRawTrackIds;
};

const getFirstLastVisibleRawTracks = (
  visibleRawTrackIds: number[],
  rawTrackRecords: RawTrackRecord[],
): [RawTrackRecord | undefined, RawTrackRecord | undefined] => {
  let firstVisibleTrack: RawTrackRecord | undefined;
  let lastVisibleTrack: RawTrackRecord | undefined;

  if (visibleRawTrackIds.length) {
    const firstVisibleId: number = visibleRawTrackIds[0];
    const lastVisibleId: number = visibleRawTrackIds[visibleRawTrackIds.length - 1];

    firstVisibleTrack = rawTrackRecords.find(({ id }) => id === firstVisibleId);
    lastVisibleTrack = rawTrackRecords.find(({ id }) => id === lastVisibleId);
  }

  return [firstVisibleTrack, lastVisibleTrack];
};

interface GetVisibleRawTrackClosestToTimeProps
  extends Pick<InvestigationState, 'rawTrackRecords' | 'hiddenAssetIds'> {
  time: number;
}

const getVisibleRawTrackClosestToTime = ({
  rawTrackRecords,
  hiddenAssetIds,
  time,
}: GetVisibleRawTrackClosestToTimeProps): RawTrackRecord | undefined => {
  let newRawTrack: RawTrackRecord | undefined;

  const visibleTracks = rawTrackRecords.filter(({ assetId }) => !hiddenAssetIds.includes(assetId));

  if (visibleTracks.length) {
    // Loop over the visible raw tracks until one is greater than the specified time.
    for (let i = 0; i < visibleTracks.length; i++) {
      const visibleRawTrack = visibleTracks[i];

      // Update the newRawTrack until the time is exceeded.
      if (visibleRawTrack && visibleRawTrack.time <= time) {
        newRawTrack = { ...visibleRawTrack };
      } else {
        // Once the time has been exceeded, break and return the newRawTrack value from the previous iteration.
        break;
      }
    }
  }

  return newRawTrack;
};

type GetPlaybackWithinTracksProps = Pick<
  InvestigationState,
  'playback' | 'firstRawTrack' | 'lastRawTrack' | 'interactionStartTime'
>;

// Pause playback if the bounds are unknown, or the current time has exceeded the bounds.
const getPlaybackWithinTracks = ({
  playback,
  firstRawTrack,
  lastRawTrack,
  interactionStartTime,
}: GetPlaybackWithinTracksProps): InvestigationPlaybackState => {
  if (!(lastRawTrack && firstRawTrack)) {
    // No tracks, so default to the interaction start time.
    return { isPlaying: false, currentTime: interactionStartTime };
  } else if (playback.currentTime >= lastRawTrack.time) {
    // Playback time is greater than the upper limit.
    return { isPlaying: false, currentTime: lastRawTrack.time };
  } else if (playback.currentTime <= firstRawTrack.time) {
    // Playback time is less than the lower limit.
    return { isPlaying: false, currentTime: firstRawTrack.time };
  } else {
    // Otherwise, return the unchanged playback state.
    return playback;
  }
};

interface GetCurrentRawTrackAndPlaybackFromTimeProps
  extends Pick<
    InvestigationState,
    | 'rawTrackRecords'
    | 'playback'
    | 'firstRawTrack'
    | 'lastRawTrack'
    | 'hiddenAssetIds'
    | 'interactionStartTime'
  > {
  time: number;
}

const getCurrentRawTrackAndPlaybackFromTime = ({
  time,
  playback,
  firstRawTrack,
  lastRawTrack,
  hiddenAssetIds,
  rawTrackRecords,
  interactionStartTime,
}: GetCurrentRawTrackAndPlaybackFromTimeProps): [
  RawTrackRecord | undefined,
  InvestigationPlaybackState,
] => {
  let newTime = time;
  let isPlaying = playback.isPlaying;

  // First, protect against absent first/last tracks data.
  if (!(firstRawTrack && lastRawTrack)) {
    return [undefined, { currentTime: interactionStartTime, isPlaying: false }];
  }

  // Constrain the time to make sure it is not outside of the raw tracks range.
  if (time < firstRawTrack.time) {
    newTime = firstRawTrack.time;
    isPlaying = false; // Stop playback so that the user does not experience a jump in time during playback.
  } else if (time > lastRawTrack.time) {
    newTime = lastRawTrack.time;
    isPlaying = false; // Stop playback if the last raw track has been reached.
  }

  // Use the constrained time to try to find a visible raw track to set as the current raw track.
  const newRawTrack = getVisibleRawTrackClosestToTime({
    rawTrackRecords,
    hiddenAssetIds,
    time: newTime,
  });

  const newPlayback: InvestigationPlaybackState = {
    currentTime: newTime,
    isPlaying,
  };

  return [newRawTrack, newPlayback];
};

interface GetEntityReturn {
  entityId: string;
  entity: Draft<InvestigationEntity> | undefined;
}

/** Get the investigation entity from state if one exists that matches the siteId and investigationId. */
const getEntity = (
  state: Draft<EntityState<InvestigationEntity, string>>,
  payload: InvestigationEnitityIdProps,
): GetEntityReturn => {
  const { siteId, investigationId } = payload;
  const entityId = getInvestigationEntityId({ siteId, investigationId });
  const entity: Draft<InvestigationEntity> | undefined = state.entities[entityId];

  return { entityId, entity };
};

const investigationsSlice = createSlice({
  name: 'investigations',
  initialState: investigationsAdapter.getInitialState(),
  reducers: {
    /** This reducer is used to initialise or update an investigation entity based on the investigation endpoint response. */
    registerInvestigation: (state, action: PayloadAction<RegisterInvestigationPayload>) => {
      const {
        data: { ts, assets },
      } = action.payload;
      const { entityId, entity } = getEntity(state, action.payload);

      if (entity) {
        const { assetControls, rawTrackRecords, interactionAssetId } = entity;

        // Filter new assets and use them to create new asset controls.
        const currentAssetIds: number[] = Object.keys(assetControls).map(Number);
        const newAssets = assets.filter(
          ({ asset: assetId }: InvestigationAssetTrackBasicModel) =>
            !currentAssetIds.includes(assetId),
        );
        const newAssetControls = createAssetControls(newAssets, interactionAssetId);

        const updatedAssetControls = { ...assetControls, ...newAssetControls };
        const hiddenAssetIds = getHiddenAssetIds(updatedAssetControls);

        // Update raw tracks with the new hidden assets list.
        const visibleRawTrackIds = getVisibleRawTrackIds(rawTrackRecords, hiddenAssetIds);

        // Update firstVisibleRawTrack and lastVisibleRawTrack because they might be affected by the new asset visibility.
        const [firstVisibleRawTrack, lastVisibleRawTrack] = getFirstLastVisibleRawTracks(
          visibleRawTrackIds,
          rawTrackRecords,
        );

        const newPlayback = getPlaybackWithinTracks({ ...entity });

        const newRawTrack = getVisibleRawTrackClosestToTime({
          rawTrackRecords,
          hiddenAssetIds,
          time: newPlayback.currentTime,
        });

        // Update the existing entity by adding any new asset controls.
        investigationsAdapter.updateOne(state, {
          id: entityId,
          changes: {
            assetControls: { ...updatedAssetControls },
            hiddenAssetIds: [...hiddenAssetIds],
            visibleRawTrackIds: [...visibleRawTrackIds],
            firstVisibleRawTrack,
            lastVisibleRawTrack,
            currentRawTrack: newRawTrack,
            playback: { ...newPlayback },
          },
        });
      } else {
        // Try to find the LO Asset in the assets list and store the ID.
        // Custom investigations will not have a LO asset.
        const { asset: interactionAssetId } =
          assets.find(({ typ }) => typ === InvestigationAssetType.Local) ?? {};

        // Initialise the timestamp to be the milliseconds since epoch of the interaction start.
        const interactionStartTime = DateTime.fromISO(ts, {
          zone: 'UTC',
        }).toMillis();

        const assetControls = createAssetControls(assets, interactionAssetId);
        const hiddenAssetIds = getHiddenAssetIds(assetControls);

        // Create a new entity.
        investigationsAdapter.addOne(state, {
          id: entityId,
          interactionStartTime,
          interactionAssetId,
          assetControls,
          hiddenAssetIds,
          playback: {
            currentTime: interactionStartTime,
            isPlaying: false,
          },
          // The following initial values will be updated by updateInvestigationWithRawTracks.
          interactionStartRawTrack: undefined,
          currentRawTrack: undefined,
          rawTrackRecords: [],
          firstRawTrack: undefined,
          lastRawTrack: undefined,
          visibleRawTrackIds: [],
          firstVisibleRawTrack: undefined,
          lastVisibleRawTrack: undefined,
        });
      }
    },
    /** This reducer can be called at any point during the playback, if the UI changes and the table remounts. */
    updateInvestigationWithRawTracks: (
      state,
      action: PayloadAction<UpdateInvestigationWithRawTracksPayload>,
    ) => {
      const { rawTrackRecords } = action.payload;
      const { entityId, entity } = getEntity(state, action.payload);

      if (!entity) return;

      const { interactionStartTime, hiddenAssetIds, interactionAssetId, playback } = entity;

      const visibleRawTrackIds = getVisibleRawTrackIds(rawTrackRecords, hiddenAssetIds);

      let interactionStartRawTrack: RawTrackRecord | undefined;

      if (typeof interactionAssetId === 'number') {
        // Find the raw track that matches the interaction start time and LO asset Id (regardless of asset visibility).
        // TODO: This lookup should change with CP-577 to look for a specific field that denotes the start of the interaction.
        interactionStartRawTrack = rawTrackRecords.find(
          ({ assetId, time }) => assetId === interactionAssetId && time === interactionStartTime,
        );
      }

      // Get the first and last raw tracks.
      const firstRawTrack: RawTrackRecord | undefined = rawTrackRecords.length
        ? rawTrackRecords[0]
        : undefined;
      const lastRawTrack: RawTrackRecord | undefined = rawTrackRecords.length
        ? rawTrackRecords[rawTrackRecords.length - 1]
        : undefined;

      const [firstVisibleRawTrack, lastVisibleRawTrack] = getFirstLastVisibleRawTracks(
        visibleRawTrackIds,
        rawTrackRecords,
      );

      const newPlayback = getPlaybackWithinTracks({
        playback,
        firstRawTrack,
        lastRawTrack,
        interactionStartTime,
      });

      const newRawTrack = getVisibleRawTrackClosestToTime({
        rawTrackRecords,
        hiddenAssetIds,
        time: newPlayback.currentTime,
      });

      investigationsAdapter.updateOne(state, {
        id: entityId,
        changes: {
          interactionStartRawTrack,
          currentRawTrack: newRawTrack,
          rawTrackRecords: [...rawTrackRecords],
          visibleRawTrackIds: [...visibleRawTrackIds],
          firstRawTrack,
          lastRawTrack,
          firstVisibleRawTrack,
          lastVisibleRawTrack,
          playback: { ...newPlayback },
        },
      });
    },
    setInvestigationAssetVisibility: (state, action: PayloadAction<SetAssetVisibilityAction>) => {
      const { assetId, visible } = action.payload;
      const { entityId, entity } = getEntity(state, action.payload);

      if (!entity) return;

      const { assetControls, rawTrackRecords } = entity;

      const updatedAssetControls = {
        ...assetControls,
        [assetId]: {
          ...assetControls[assetId],
          visible,
        },
      };

      const hiddenAssetIds = getHiddenAssetIds(updatedAssetControls);

      // Update which raw tracks are visible.
      const visibleRawTrackIds = getVisibleRawTrackIds(rawTrackRecords, hiddenAssetIds);

      const [firstVisibleRawTrack, lastVisibleRawTrack] = getFirstLastVisibleRawTracks(
        visibleRawTrackIds,
        rawTrackRecords,
      );

      const newPlayback = getPlaybackWithinTracks({ ...entity });

      const newRawTrack = getVisibleRawTrackClosestToTime({
        rawTrackRecords,
        hiddenAssetIds,
        time: newPlayback.currentTime,
      });

      investigationsAdapter.updateOne(state, {
        id: entityId,
        changes: {
          assetControls: { ...updatedAssetControls },
          hiddenAssetIds: [...hiddenAssetIds],
          visibleRawTrackIds: [...visibleRawTrackIds],
          currentRawTrack: newRawTrack,
          firstVisibleRawTrack,
          lastVisibleRawTrack,
          playback: {
            ...newPlayback,
          },
        },
      });
    },
    stepRawTrack: (state, action: PayloadAction<StepRawTrackAction>) => {
      const { direction } = action.payload;
      const { entityId, entity } = getEntity(state, action.payload);

      if (!entity) return;

      const {
        rawTrackRecords,
        currentRawTrack: oldRawTrack,
        visibleRawTrackIds,
        playback,
      } = entity;

      // Update the current raw track so that a visible one is selected.
      let newRawTrackId: number | undefined;
      let newRawTrack: RawTrackRecord | undefined;

      // The current track should only be undefined if the current time is outside of the range of the visible raw tracks.
      if (typeof oldRawTrack?.id === 'number') {
        const currentRawTrackIndex = visibleRawTrackIds.findIndex(id => id === oldRawTrack.id);
        newRawTrackId = visibleRawTrackIds[currentRawTrackIndex + direction];
      }
      // If there are visible raw tracks, then move to the nearest one relative to the step direction.
      else if (visibleRawTrackIds.length) {
        // Get either the first or last visible raw track, depending on the direction.
        newRawTrackId = visibleRawTrackIds[direction === 1 ? 0 : visibleRawTrackIds.length - 1];
      }

      if (typeof newRawTrackId === 'number') {
        // Find the new currentRawTrack.
        newRawTrack = rawTrackRecords.find(({ id }) => id === newRawTrackId);
      }

      // Update the playback time with the timestamp of the newly selected raw track.
      const currentTime = newRawTrack ? newRawTrack.time : playback.currentTime;

      investigationsAdapter.updateOne(state, {
        id: entityId,
        changes: {
          currentRawTrack: newRawTrack,
          playback: {
            currentTime,
            isPlaying: false,
          },
        },
      });
    },
    setCurrentTrack: (state, action: PayloadAction<SetCurrentRawTrackAction>) => {
      const { entityId, entity } = getEntity(state, action.payload);

      if (!entity) return;

      const {
        rawTrackRecords,
        visibleRawTrackIds,
        currentRawTrack: oldRawTrack,
        interactionStartTime,
      } = entity;
      const { id: newRawTrackId } = action.payload;

      let newRawTrack: RawTrackRecord | undefined = oldRawTrack;

      // Update the current raw track if it is visible and is a valid id.
      if (visibleRawTrackIds.includes(newRawTrackId)) {
        newRawTrack = rawTrackRecords.find(({ id }) => id === newRawTrackId);
      }

      // Update the playback time to match the current raw track or fallback to the existing playback time.
      const currentTime = newRawTrack?.time ?? interactionStartTime;

      investigationsAdapter.updateOne(state, {
        id: entityId,
        changes: {
          currentRawTrack: newRawTrack,
          playback: {
            currentTime,
            isPlaying: false,
          },
        },
      });
    },
    setIsPlaying: (state, action: PayloadAction<SetIsPlayingAction>) => {
      const { entityId, entity } = getEntity(state, action.payload);

      if (!entity) return;

      const { playback } = entity;
      const { isPlaying } = action.payload;

      investigationsAdapter.updateOne(state, {
        id: entityId,
        changes: {
          playback: {
            ...playback,
            isPlaying,
          },
        },
      });
    },
    setCurrentTime: (state, action: PayloadAction<SetCurrentTimeAction>) => {
      const { entityId, entity } = getEntity(state, action.payload);

      if (!entity) return;

      const { currentTime } = action.payload;

      const [newRawTrack, newPlayback] = getCurrentRawTrackAndPlaybackFromTime({
        ...entity,
        time: currentTime,
      });

      investigationsAdapter.updateOne(state, {
        id: entityId,
        changes: {
          currentRawTrack: newRawTrack,
          playback: { ...newPlayback },
        },
      });
    },
  },
});

export const { selectById: selectInvestigationById } = investigationsAdapter.getSelectors(
  (state: RootState) => state.investigations,
);

export const {
  registerInvestigation,
  updateInvestigationWithRawTracks,
  setInvestigationAssetVisibility,
  stepRawTrack,
  setCurrentTrack,
  setIsPlaying,
  setCurrentTime,
} = investigationsSlice.actions;

export default investigationsSlice.reducer;

export const getInvestigationEntityId = ({
  siteId,
  investigationId,
}: InvestigationEnitityIdProps): string => {
  // The siteId is part of the entityId to prevent conflicts with numerical ids across different sites.
  return `${siteId}--${investigationId}`;
};

export const useInvestigationEntity = ({
  siteId,
  investigationId,
}: Partial<InvestigationEnitityIdProps>): Partial<InvestigationState> => {
  const entityId: string | undefined =
    typeof siteId !== 'undefined' && typeof investigationId !== 'undefined'
      ? getInvestigationEntityId({ siteId, investigationId })
      : undefined;

  const entity: InvestigationEntity | undefined = useAppSelector(state =>
    entityId ? selectInvestigationById(state, entityId) : undefined,
  );

  const { id, ...entityState } = entity ?? {};

  return entityState;
};

interface SelectAssetControlProps extends Partial<InvestigationEnitityIdProps> {
  assetId: number;
}

export const useSelectAssetControl = ({
  assetId,
  siteId,
  investigationId,
}: SelectAssetControlProps): AssetControl | undefined => {
  const { assetControls = {} } = useInvestigationEntity({
    siteId,
    investigationId,
  });

  return useMemo<AssetControl | undefined>(() => assetControls[assetId], [assetControls, assetId]);
};
